/**
 * FSH research page contents list.
 *
 * One list serves the whole page. This sets the active entry, the progress
 * fills, the horizontal centring of the active title on a phone, and the
 * sub-entries a section offers for its own cards.
 *
 * Position is left to CSS. The list is fixed, so nothing here moves it. The
 * only thing this hides is the list itself, outside the article.
 */
(function () {
  if (window.__fshTocReady) return;
  window.__fshTocReady = true;

  function init() {
    var navs = Array.prototype.slice.call(document.querySelectorAll('[data-fsh-toc]'));
    if (!navs.length) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var behavior = reduceMotion ? 'auto' : 'smooth';
    var ticking = false;

    var lists = navs.map(function (nav) {
      var links = Array.prototype.slice.call(nav.querySelectorAll('[data-fsh-toc-link]'));
      var entries = links
        .map(function (link) {
          var el = document.getElementById(link.getAttribute('data-fsh-toc-link'));
          return el ? { link: link, el: el } : null;
        })
        .filter(Boolean);

      if (!entries.length) {
        nav.hidden = true;
        return null;
      }

      return {
        nav: nav,
        wrap: nav.closest('.fsh-contents'),
        entries: entries,
        list: nav.querySelector('[data-fsh-toc-list]'),
        rail: nav.querySelector('[data-fsh-rail]'),
        progress: nav.querySelector('[data-fsh-progress]'),
        offset: parseInt(nav.getAttribute('data-fsh-offset'), 10) || 32,
        active: null
      };
    }).filter(Boolean);

    if (!lists.length) return;

    // The offset is a section setting, and the CSS holds its own default.
    // Tie them together, because the placement below measures from the line
    // the wrapper sits on.
    lists.forEach(function (group) {
      if (group.wrap) {
        group.wrap.style.setProperty('--fsh-toc-top', group.offset + 'px');
      }
    });

    // The article is every content section. It is the container the list
    // would have been sticky inside, if sibling sections could share one.
    var article = Array.prototype.slice.call(document.querySelectorAll('.fsh-section'));

    // On a phone the theme's header is sticky and slides in and out as the
    // reader changes direction. It also sits above this list. Follow its
    // lower edge, so the bar sits under the header instead of behind it.
    var siteHeader = document.querySelector('.section-header');

    // The phone layout is a bar across the top, which cannot travel in from
    // below the way the column does.
    var column = window.matchMedia('(min-width: 1024px)');

    function showOverArticle() {
      if (!article.length) return;
      var first = article[0].getBoundingClientRect();
      var top = first.top;
      var bottom = article[article.length - 1].getBoundingClientRect().bottom;
      var shift = siteHeader
        ? Math.max(0, siteHeader.getBoundingClientRect().bottom)
        : 0;

      // Where the list sits inside the first section, read from the layout
      // rather than assumed, so it tracks the section's own padding.
      var body = article[0].querySelector('.fsh-section__body');
      var inset = body ? body.getBoundingClientRect().top - top : 0;

      // Where the content of the last section ends. The list leaves with this
      // edge, not the section's outer one, so it respects the section's
      // bottom padding the same way the inset respects the top. Measured
      // against the outer edge, the rail ran into the footer.
      var lastBody = article[article.length - 1].querySelector('.fsh-section__body');
      var end = lastBody ? lastBody.getBoundingClientRect().bottom : bottom;

      lists.forEach(function (group) {
        if (group.wrap) group.wrap.style.setProperty('--fsh-toc-shift', shift + 'px');

        var height = group.nav.offsetHeight;

        if (!column.matches) {
          group.nav.style.top = Math.min(0, bottom - group.offset - height) + 'px';
          var inside = top <= group.offset && bottom >= group.offset;
          group.nav.classList.toggle('fsh-toc--hidden', !inside);
          return;
        }

        // Sticky, written out. The list rides up with the first section,
        // holds at the offset once it arrives, and is pushed out by the end
        // of the last section. Being fixed, it cannot feel any of that, so
        // each step is measured here.
        //
        // Placing it this way is what makes it arrive rather than appear.
        // Hiding it until it reached the offset made it pop into place.
        //
        // The line it holds at drops beneath the theme's header while that is
        // showing, so a reader scrolling up gets both. The CSS moves the
        // wrapper by the same shift.
        var pin = group.offset + shift;
        var placed = Math.min(Math.max(top + inset, pin), end - height);
        group.nav.style.top = placed - pin + 'px';
        group.nav.classList.remove('fsh-toc--hidden');
      });
    }

    function centreInBar(group, link) {
      var list = group.list;
      if (!list || list.scrollWidth <= list.clientWidth) return;
      var target = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
      var max = list.scrollWidth - list.clientWidth;
      list.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: behavior });
    }

    // The item carries the state as well as the link: the column fades and
    // opens sub-entries by item, the phone bar underlines by link.
    function setActive(group, entry) {
      if (entry === group.active) return;
      group.active = entry;
      group.entries.forEach(function (candidate) {
        var on = candidate === entry;
        candidate.link.classList.toggle('is-active', on);
        if (candidate.link.parentNode) candidate.link.parentNode.classList.toggle('is-active', on);
      });
      centreInBar(group, entry.link);
    }

    // The theme's header hides on the way down and slides back in on the way
    // up. A jump upward would bring it in over the list and over the heading
    // being landed. The header listens for preventHeaderReveal and stays
    // hidden until the scroll stops, which is how the theme's own media
    // gallery scrolls. Only sent going up: sent going down, it would swallow
    // the reader's next scroll up.
    var stickyHeader = document.querySelector('sticky-header');

    // The header's menus open on CSS :hover. When the header slides in under
    // a pointer that has not moved, the browser counts that as hovering and a
    // menu bursts open on a reader who only scrolled. This page parks the
    // pointer in the top left, over the list, which is exactly where those
    // menus live. So hold the header inert from the moment it starts to slide
    // in until the pointer really moves. A class on the root does it, with
    // pointer-events: none in the stylesheet.
    //
    // A mutation observer catches the reveal in the same frame the header's
    // class changes, before any hover can be drawn.
    var root = document.documentElement;
    var pointer = null;
    var settledAt = null;

    function unsettle() {
      settledAt = null;
      root.classList.remove('fsh-header-settling');
    }

    window.addEventListener('mousemove', function (event) {
      pointer = { x: event.clientX, y: event.clientY };
      // Browsers send moves at the same spot after a scroll. Only a real move
      // of the mouse lets the header respond again.
      if (settledAt && (Math.abs(pointer.x - settledAt.x) > 2 || Math.abs(pointer.y - settledAt.y) > 2)) {
        unsettle();
      }
    }, { passive: true });

    if (siteHeader && 'MutationObserver' in window) {
      new MutationObserver(function (records) {
        records.forEach(function (record) {
          var was = record.oldValue || '';
          var wasHidden = was.indexOf('shopify-section-header-hidden') !== -1;
          var isHidden = siteHeader.classList.contains('shopify-section-header-hidden');
          var isSticky = siteHeader.classList.contains('shopify-section-header-sticky');
          if (wasHidden && !isHidden && isSticky && pointer) {
            settledAt = pointer;
            root.classList.add('fsh-header-settling');
          } else if (isHidden || !isSticky) {
            unsettle();
          }
        });
      }).observe(siteHeader, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    }

    function scrollToEl(group, el) {
      var from = window.pageYOffset || document.documentElement.scrollTop;
      var top = el.getBoundingClientRect().top + from - group.offset;
      if (top < from && stickyHeader) {
        stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
      window.scrollTo({ top: top, behavior: behavior });
      history.replaceState(null, '', '#' + el.id);
    }

    // A section can offer sub-entries: any element inside it that carries
    // data-fsh-toc-sub and an id. They are listed under the section's own
    // entry, and open only while that section is the current one. The review
    // section uses this for its evidence cards, so the list follows the cards
    // with nothing to keep in step by hand.
    function buildSubs(group, entry) {
      var targets = Array.prototype.slice.call(entry.el.querySelectorAll('[data-fsh-toc-sub][id]'));
      var item = entry.link.parentNode;
      if (!targets.length || !item || item.querySelector('.fsh-toc__sub')) return;

      var sub = document.createElement('div');
      sub.className = 'fsh-toc__sub';
      var list = document.createElement('ul');
      list.className = 'fsh-toc__sublist';

      targets.forEach(function (target) {
        var li = document.createElement('li');
        li.className = 'fsh-toc__subitem fsh-body-2';
        var link = document.createElement('a');
        link.className = 'fsh-toc__sublink';
        link.href = '#' + target.id;
        link.textContent = target.getAttribute('data-fsh-toc-sub');
        link.addEventListener('click', function (event) {
          event.preventDefault();
          scrollToEl(group, target);
        });
        li.appendChild(link);
        list.appendChild(li);
      });

      sub.appendChild(list);
      item.appendChild(sub);
    }

    function update() {
      ticking = false;
      showOverArticle();

      lists.forEach(function (group) {
        var entries = group.entries;

        // An entry becomes current once its section passes a line a third of
        // the way down the window, not the offset at the very top. Sections
        // carry 64 of padding at each end, so there is a 128 band of white
        // between two sections' content. With the line at the offset, the
        // reader was plainly reading the next section while the list still
        // lit the last one. It also absorbs a smooth scroll that stops a
        // fraction of a pixel short of the offset.
        var line = Math.max(group.offset, Math.round(window.innerHeight / 3));

        var current = entries[0];
        entries.forEach(function (entry) {
          if (entry.el.getBoundingClientRect().top - line <= 0) {
            current = entry;
          }
        });
        setActive(group, current);

        // Every entry owns an equal share of the rail, and reading through a
        // section fills its share. Measuring raw scroll instead would let a
        // long section swallow most of the rail and a short one barely move
        // it. The rail grows upward from the bottom, the phone bar rightward.
        var index = entries.indexOf(current);
        var box = current.el.getBoundingClientRect();
        var within = box.height > 0 ? (line - box.top) / box.height : 0;
        within = Math.max(0, Math.min(1, within));
        var ratio = (index + within) / entries.length;
        var pct = (ratio * 100).toFixed(2) + '%';

        if (group.rail) group.rail.style.height = pct;
        if (group.progress) group.progress.style.width = pct;
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    lists.forEach(function (group) {
      group.entries.forEach(function (entry) {
        buildSubs(group, entry);
        entry.link.addEventListener('click', function (event) {
          event.preventDefault();
          scrollToEl(group, entry.el);
          setActive(group, entry);
        });
      });
    });

    // The observer keeps the list correct at section boundaries even when the
    // browser throttles animation frames.
    if ('IntersectionObserver' in window) {
      var seen = [];
      var io = new IntersectionObserver(update, { threshold: [0, 0.01, 0.5, 0.99, 1] });
      lists.forEach(function (group) {
        group.entries.forEach(function (entry) {
          if (seen.indexOf(entry.el) === -1) {
            seen.push(entry.el);
            io.observe(entry.el);
          }
        });
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // The header finishes sliding after the scroll stops, so the last scroll
    // frame reads its old position. Measure again when it settles.
    if (siteHeader) {
      siteHeader.addEventListener('transitionend', onScroll);
      siteHeader.addEventListener('animationend', onScroll);
    }

    update();
  }

  // Content sections render after the first list, so wait for the document
  // before looking for the anchors.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
